import { QuestionCircleOutlined } from '@ant-design/icons'
import type { Theme } from '@mui/material'
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Popover, Typography } from '@mui/material'
import { SearchProvider } from 'contexts/SearchContext'
import type { FC } from 'react'
import { useEffect, useState } from 'react'
import { RowType } from 'types/comp-allocations'
import type { ExpandedTarget } from 'types/components'
import AdvancedRowEditor from './AdvancedRowEditor'
import SimpleRowEditor from './SimpleRowEditor'

interface RowManagerProps {
  isOpen: boolean
  tableIndex: number
  rowIndex: number
  rowLabel: string
  targets: ExpandedTarget[]
  organizationLabels: Record<string, string>
  periodId: string
  periodLabel: string
  exampleSalary: number
  variableCompensation: number
  currentRequirements: string[]
  currentOptions: string[]
  allocationPercentage: number
  minAllocationPercentage: number
  maxAllocationPercentage: number
  defaultPayoutPercent: number
  minPayoutPercent: number
  maxPayoutPercent: number
  handleClose: () => void
  handleFormulaChange: (tableIndex: number, rowIndex: number, requirements: string[], options: string[]) => void
  handleUpdateAllocations: (
    tableIndex: number,
    rowIndex: number,
    allocationPercentage: number,
    minAllocationPercentage: number,
    maxAllocationPercentage: number,
    defaultPayoutPercent: number,
    minPayoutPercent: number,
    maxPayoutPercent: number
  ) => void
  accelerator: string
  handleUpdateAccelerator: (tableIndex: number, rowIndex: number, accelerator: string) => void
  handleRowUpdate: (
    tableIndex: number,
    rowIndex: number,
    newFormulaRequirements: string[],
    newFormulaOptions: string[],
    allocationPercentage: number,
    minAllocationPercentage: number,
    maxAllocationPercentage: number,
    defaultPayoutPercent: number,
    minPayoutPercent: number,
    maxPayoutPercent: number,
    newAccelerator: string
  ) => void
  addTargetIfNotExists: (newTarget: ExpandedTarget) => void
  theme: Theme
  currency: string
  currencyDecimals: number
  rowType: RowType
}

const RowManager: FC<RowManagerProps> = ({
  isOpen,
  tableIndex,
  rowIndex,
  rowLabel,
  handleClose,
  handleFormulaChange,
  targets,
  organizationLabels,
  periodId,
  periodLabel,
  exampleSalary,
  variableCompensation,
  currentRequirements: initialRequirements,
  currentOptions: initialOptions,
  allocationPercentage,
  minAllocationPercentage,
  maxAllocationPercentage,
  defaultPayoutPercent,
  minPayoutPercent,
  maxPayoutPercent,
  handleUpdateAllocations,
  accelerator,
  handleUpdateAccelerator,
  handleRowUpdate,
  addTargetIfNotExists,
  theme,
  currency,
  currencyDecimals,
  rowType,
}) => {
  console.log(`[RowManager] Component render - tableIndex: ${tableIndex}, rowIndex: ${rowIndex}`)
  console.log(`  isOpen:`, isOpen)
  console.log(`  initialRequirements:`, initialRequirements)
  console.log(`  initialOptions:`, initialOptions)
  console.log(`  targets prop:`, targets)
  console.log(`  targets available:`, targets?.length || 0)
  console.log(`  periodId:`, periodId)

  const [isSimple, setIsSimple] = useState<boolean>(true)
  const [currentRequirements, setCurrentRequirements] = useState<string[]>(initialRequirements)
  const [currentOptions, setCurrentOptions] = useState<string[]>(initialOptions)
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  // Update local state when props change
  useEffect(() => {
    console.log(`[RowManager] useEffect updating local state:`)
    console.log(`  new initialRequirements:`, initialRequirements)
    console.log(`  new initialOptions:`, initialOptions)
    console.log(`  current currentRequirements:`, currentRequirements)
    console.log(`  current currentOptions:`, currentOptions)

    setCurrentRequirements(initialRequirements)
    setCurrentOptions(initialOptions)
  }, [initialRequirements, initialOptions])

  const localHandleFormulaChange = (requirements: string[], options: string[]) => {
    console.log(`[RowManager] localHandleFormulaChange called:`)
    console.log(`  new requirements:`, requirements)
    console.log(`  new options:`, options)

    setCurrentRequirements(requirements)
    setCurrentOptions(options)
    handleFormulaChange(tableIndex, rowIndex, requirements, options)
  }

  const toggleEditor = () => {
    if (canUseSimpleEditor) {
      setIsSimple((prev) => !prev)
    }
  }

  const formulasContain = (formula: string) => {
    const result =
      currentRequirements.some((req) => req.includes(formula)) || currentOptions.some((opt) => opt.includes(formula))
    console.log(`[RowManager] formulasContain("${formula}") = ${result}`)
    console.log(`  currentRequirements:`, currentRequirements)
    console.log(`  currentOptions:`, currentOptions)
    return result
  }

  // When editing existing rows, include targets from all periods to find targets referenced by the row
  // When creating new rows, restrict to current period only
  const filteredTargets =
    targets !== undefined
      ? currentRequirements.length > 0 || currentOptions.length > 0
        ? targets // Include all targets when editing existing rows
        : targets.filter((target) => target.target.periodId === periodId) // Restrict to current period for new rows
      : []

  console.log(`[RowManager] Target filtering debug:`)
  console.log(`  targets available:`, targets?.length || 0)
  console.log(`  currentRequirements:`, currentRequirements)
  console.log(`  currentOptions:`, currentOptions)
  console.log(`  periodId:`, periodId)
  console.log(`  hasExistingData:`, currentRequirements.length > 0 || currentOptions.length > 0)
  console.log(`  filteredTargets:`, filteredTargets.length)

  // A component slug in the formula matches that component's target in every period, so only
  // count it for the row's own period. Targets from other periods count only when their own slug
  // is referenced, and row-period targets sort first so editors preselect the correct-period
  // target instead of e.g. Q2's target on a Q3 row (which then gets written back into the formula).
  const currentTargetsUsed = filteredTargets
    .filter((target) => {
      const isRowPeriod = target.target.periodId === periodId
      const targetSlugUsed = formulasContain(target.target.slug)
      const componentSlugUsed = isRowPeriod && formulasContain(target.component.slug)
      const isUsed = targetSlugUsed || componentSlugUsed
      console.log(`[RowManager] Target ${target.target.slug} (component: ${target.component.slug}) - used: ${isUsed}`)
      return isUsed
    })
    .sort((a, b) => Number(b.target.periodId === periodId) - Number(a.target.periodId === periodId))

  console.log(`[RowManager] Final currentTargetsUsed:`, currentTargetsUsed.length, 'targets')
  currentTargetsUsed.forEach((target, index) => {
    console.log(`  Target ${index + 1}: ${target.target.slug} (${target.component.slug})`)
  })

  useEffect(() => {
    // Determine if simple editor can be used
    const canUseSimpleEditor = currentTargetsUsed.length <= 1 && currentOptions.length <= 1

    console.log(`[RowManager] useEffect determining editor type:`)
    console.log(`  currentTargetsUsed.length:`, currentTargetsUsed.length)
    console.log(`  currentOptions.length:`, currentOptions.length)
    console.log(`  canUseSimpleEditor:`, canUseSimpleEditor)
    console.log(`  current isSimple:`, isSimple)

    // Update editor state when the condition changes
    if (!canUseSimpleEditor) {
      console.log(`[RowManager] Switching to Advanced Editor`)
      setIsSimple(false)
    }
  }, [currentOptions, currentTargetsUsed, isSimple])

  const handleAllocationChange = (
    allocationPercentage: number,
    minAllocationPercentage: number,
    maxAllocationPercentage: number,
    defaultPayoutPercent: number,
    minPayoutPercent: number,
    maxPayoutPercent: number
  ) => {
    handleUpdateAllocations(
      tableIndex,
      rowIndex,
      allocationPercentage,
      minAllocationPercentage,
      maxAllocationPercentage,
      defaultPayoutPercent,
      minPayoutPercent,
      maxPayoutPercent
    )
  }

  useEffect(() => {
    // Update local state when props change
  }, [currentRequirements, currentOptions])

  const canUseSimpleEditor =
    currentTargetsUsed.length <= 1 &&
    currentOptions.length <= 1 &&
    rowType !== RowType.FIXED_VALUE_ROW &&
    rowType !== RowType.DISPLAY_ROW

  const buttonLabel = isSimple ? 'Show Advanced Editor' : 'Show Simple Editor'

  const handlePopoverOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handlePopoverClose = () => {
    setAnchorEl(null)
  }

  const handleClearAndSwitch = () => {
    setCurrentRequirements([])
    setCurrentOptions([])
    handleFormulaChange(tableIndex, rowIndex, [], [])
    setIsSimple(true)
    handlePopoverClose()
  }

  const open = Boolean(anchorEl)

  return (
    <SearchProvider>
      <Dialog
        open={isOpen}
        onClose={handleClose}
        maxWidth={false}
        fullWidth
        fullScreen
        PaperProps={{
          style: {
            width: '100%',
            maxWidth: '100%',
            height: '100%',
            margin: 0,
            padding: 15,
          },
        }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Typography variant="h4" sx={{ mb: { xs: 2, sm: 0 } }}>
              {rowLabel !== '' ? `Customize ${rowLabel} Variable Compensation` : 'Customize Row'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button onClick={toggleEditor} disabled={!canUseSimpleEditor} sx={{ mr: 1 }}>
                {buttonLabel}
              </Button>
              {!canUseSimpleEditor && (
                <IconButton onClick={handlePopoverOpen}>
                  <QuestionCircleOutlined />
                </IconButton>
              )}
              <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handlePopoverClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
              >
                <Box sx={{ p: 2 }}>
                  <Typography sx={{ mb: 2 }}>Clear selected targets and formulas to use the Simple Editor?</Typography>
                  <Button onClick={handleClearAndSwitch}>Clear and Switch</Button>
                </Box>
              </Popover>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {isSimple && canUseSimpleEditor ? (
            <SimpleRowEditor
              organizationLabels={organizationLabels}
              periodLabel={periodLabel}
              periodId={periodId}
              exampleSalary={exampleSalary}
              variableCompensation={variableCompensation}
              handleCloseModal={handleClose}
              handleFormulaChange={localHandleFormulaChange}
              handleUpdateAllocations={handleAllocationChange}
              tableIndex={tableIndex}
              rowIndex={rowIndex}
              acceleratorInitial={accelerator}
              handleUpdateAccelerator={handleUpdateAccelerator}
              handleRowUpdate={handleRowUpdate}
              addTargetIfNotExists={addTargetIfNotExists}
              theme={theme}
              currency={currency}
              currencyDecimals={currencyDecimals}
              rowType={rowType}
              initialRequirements={currentRequirements}
              initialOptions={currentOptions}
              initialAllocationValues={[minAllocationPercentage, allocationPercentage, maxAllocationPercentage]}
              initialMaxPayout={[minPayoutPercent, defaultPayoutPercent, maxPayoutPercent]}
              currentTargetsUsed={currentTargetsUsed}
            />
          ) : (
            <AdvancedRowEditor
              tableIndex={tableIndex}
              rowIndex={rowIndex}
              organizationLabels={organizationLabels}
              periodLabel={periodLabel}
              periodId={periodId}
              exampleSalary={exampleSalary}
              variableCompensation={variableCompensation}
              currentRequirements={currentRequirements}
              currentOptions={currentOptions}
              currentTargetsUsed={currentTargetsUsed}
              allocationPercentage={allocationPercentage}
              minAllocationPercentage={minAllocationPercentage}
              maxAllocationPercentage={maxAllocationPercentage}
              defaultPayoutPercent={defaultPayoutPercent}
              minPayoutPercent={minPayoutPercent}
              maxPayoutPercent={maxPayoutPercent}
              handleFormulaChange={localHandleFormulaChange}
              handleUpdateAllocations={handleAllocationChange}
              handleCloseModal={handleClose}
              acceleratorInitial={accelerator}
              handleUpdateAccelerator={handleUpdateAccelerator}
              handleRowUpdate={handleRowUpdate}
              addTargetIfNotExists={addTargetIfNotExists}
              theme={theme}
              currency={currency}
              currencyDecimals={currencyDecimals}
              rowType={rowType}
            />
          )}
        </DialogContent>
      </Dialog>
    </SearchProvider>
  )
}

export default RowManager
